import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

// Hook pro animace při scrollování
function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
}

// Komponenta pro animovanou sekci
function AnimatedSection({ 
  children, 
  className = '', 
  animation = 'fade-up' 
}: { 
  children: React.ReactNode; 
  className?: string;
  animation?: 'fade-up' | 'fade-left' | 'fade-right' | 'scale';
}) {
  const { ref, isVisible } = useScrollAnimation();

  const animationClasses = {
    'fade-up': `transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`,
    'fade-left': `transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`,
    'fade-right': `transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`,
    'scale': `transition-all duration-700 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`,
  };

  return (
    <div ref={ref} className={`${animationClasses[animation]} ${className}`}>
      {children}
    </div>
  );
}

export default function PartnershipPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    experience: '',
    availability: '',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Simulace odeslání formuláře
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        experience: '',
        availability: '',
        message: ''
      });
    } catch (error) {
      console.error('Chyba při odesílání:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title={t('seo.partner.title')}
        description={t('seo.partner.description')}
        keywords={t('seo.partner.keywords')}
      />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/4ef065a9614f0686a01c4e5210db0eea.jpg"
              alt="Partnership"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white/60"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-gray-900 leading-tight">
              Chceme být i ve vaší lokalitě.
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-800 mb-4 max-w-2xl mx-auto leading-relaxed">
              Vracíme lidem čas. Dáváme práci smysl.
            </p>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-800 font-semibold max-w-2xl mx-auto">
              Hledáme partnery, ne pobočky.
            </p>
          </div>
        </section>

        {/* Expansion Section */}
        <section className="py-16 bg-gradient-to-br from-pink-50 to-teal-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection animation="fade-up" className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full text-teal-700 text-sm font-medium mb-4">
                <i className="ri-map-pin-line text-lg"></i>
                <span>Expanze po celé ČR</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Míříme i za vámi
              </h2>
              <div className="text-xl text-gray-600 max-w-3xl mx-auto space-y-2">
                <p>Vyžehlené stavíme jako síť lokálních partnerů, kteří sdílí stejné hodnoty:</p>
                <p>ruční práci, férovost a respekt k lidem i času.</p>
                <p>Jedeme do lokalit po celé České republice.</p>
                <p>Hledáme partnery, kteří s námi chtějí dělat smysluplnou práci pod jednou značkou.</p>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-8">
              <AnimatedSection animation="fade-up" className="delay-100">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all h-full">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                    <i className="ri-map-2-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Celá Česká republika
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Aktivně hledáme partnery do všech krajů a měst. Vaše lokalita může být další.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" className="delay-200">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all h-full">
                  <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-6">
                    <i className="ri-community-line text-2xl text-pink-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Lokální přítomnost
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Buďte tváří VyŽehlené ve své lokalitě a budujte vztahy s místními klienty.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" className="delay-300">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all h-full">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                    <i className="ri-rocket-line text-2xl text-emerald-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Společný růst
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Rosteme společně – silná značka, jednotný přístup, lokální působení.
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Why Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <AnimatedSection animation="fade-right">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full text-teal-700 text-sm font-medium mb-6">
                  <i className="ri-lightbulb-line text-lg"></i>
                  <span>Proč to děláme</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Lidé dnes nemají<br />málo peněz.<br />Mají málo času.
                </h2>
                <div className="space-y-4 text-lg text-gray-600">
                  <p className="leading-relaxed">
                    Žehlení není problém.<br />
                    <strong className="text-gray-900">Problem je, kolik času a energie bere.</strong>
                  </p>
                  <p className="leading-relaxed">
                    VyŽehlené vzniklo proto, aby:
                  </p>
                  <ul className="space-y-3 ml-6">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                      <span>lidé měli více času</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                      <span>rodiny více klidu</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-2xl text-teal-600 mt-1"></i>
                      <span>profíci více prostoru na to, co je pro ně důležité</span>
                    </li>
                  </ul>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-left">
                <div className="relative">
                  <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20laundry%20service%20worker%20carefully%20ironing%20clothes%20in%20modern%20bright%20workspace%20with%20organized%20clean%20environment%20showing%20attention%20to%20detail%20and%20quality%20craftsmanship%20soft%20natural%20lighting%20minimalist%20background&width=600&height=600&seq=partner-why-1&orientation=squarish"
                      alt="Profesionální žehlení"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-3xl -z-10"></div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Proč to děláme - s grafikou */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Levá strana - grafika */}
              <div className="relative">
                {/* Hlavní obrázek */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=Beautifully%20ironed%20and%20neatly%20folded%20clothes%20including%20dress%20shirts%20blouses%20trousers%20and%20dresses%20arranged%20on%20a%20clean%20white%20surface%20with%20soft%20natural%20morning%20light%20creating%20gentle%20shadows%20minimalist%20aesthetic%20professional%20laundry%20service%20quality%20fresh%20and%20crisp%20fabrics%20peaceful%20organized%20atmosphere%20simple%20clean%20background&width=600&height=700&seq=partner-ironed-clothes-v2&orientation=portrait"
                    alt="Vyžehlené oblečení"
                    className="w-full h-[500px] object-cover"
                  />
                </div>

                {/* Plovoucí kartička */}
                <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 border-2 border-teal-500">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <i className="ri-time-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">+4 hodiny</div>
                      <div className="text-sm text-gray-600">ušetřený čas</div>
                    </div>
                  </div>
                </div>

                {/* Dekorativní prvky */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-teal-100 rounded-full opacity-50 blur-2xl"></div>
                <div className="absolute -bottom-8 left-1/3 w-32 h-32 bg-teal-200 rounded-full opacity-30 blur-3xl"></div>
              </div>

              {/* Pravá strana - text */}
              <AnimatedSection animation="fade-left">
                <div className="lg:pl-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-100 to-emerald-100 rounded-full text-teal-700 text-sm font-medium mb-6">
                    <i className="ri-sparkling-line text-lg"></i>
                    <span>Proč to děláme</span>
                  </div>
                  
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                    Čas je dnes vzácnější<br/>než peníze
                  </h2>

                  <div className="bg-gradient-to-br from-teal-50 via-white to-emerald-50 rounded-2xl p-8 border border-teal-100 shadow-lg">
                    <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-lightbulb-flash-line text-xl text-teal-600"></i>
                        </div>
                        <p>
                          Žehlení samo o sobě problém není.<br/>
                          <span className="text-gray-500">Tím je všechno kolem.</span>
                        </p>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-heart-pulse-line text-xl text-emerald-600"></i>
                        </div>
                        <p className="font-semibold text-gray-900">
                          VyŽehlené vrací čas, klid a prostor<br/>
                          <span className="text-teal-600">lidem, rodinám i profesionálům.</span>
                        </p>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-focus-3-line text-xl text-pink-600"></i>
                        </div>
                        <p>
                          Některé věci jsou malé.<br/>
                          Ale berou hodně času.<br/>
                          VyŽehlené vzniklo proto,<br/>
                          <span className="font-medium text-gray-900">aby ho lidé měli víc tam, kde na něm záleží.</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Spodní text */}
            <AnimatedSection animation="fade-up" className="text-center mt-16">
              <div className="inline-flex items-center gap-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white px-8 py-4 rounded-full shadow-xl">
                <i className="ri-team-line text-2xl"></i>
                <p className="text-xl font-bold">
                  A my hledáme partnery, kteří to cítí stejně.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Hlubší smysl */}
        <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-teal-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection animation="scale">
              <div className="bg-white rounded-3xl p-10 md:p-14 shadow-xl border border-teal-100">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full text-teal-700 text-sm font-medium mb-6">
                    <i className="ri-heart-line text-lg"></i>
                    <span>Hlubší smysl</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-8">
                    Lidé dnes nemají málo peněz.<br/>
                    Mají málo času.
                  </h2>
                </div>

                <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
                  <div className="text-center">
                    <p className="text-xl">
                      <strong className="text-gray-900">Žehlení není problém.</strong><br/>
                      Problem je, kolik času a energie bere.
                    </p>
                  </div>

                  <div className="text-center space-y-4 mt-8">
                    <p className="text-xl font-semibold text-gray-900">VyŽehlené vzniklo proto, aby:</p>
                    
                    <div className="space-y-4 max-w-2xl mx-auto">
                      <div className="flex items-start gap-3">
                        <i className="ri-time-line text-teal-600 text-2xl mt-1 flex-shrink-0"></i>
                        <p className="text-lg text-gray-900"><strong>lidé měli více času</strong></p>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-home-heart-line text-teal-600 text-2xl mt-1 flex-shrink-0"></i>
                        <p className="text-lg text-gray-900"><strong>rodiny více klidu</strong></p>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-focus-line text-teal-600 text-2xl mt-1 flex-shrink-0"></i>
                        <p className="text-lg text-gray-900"><strong>abychom měli více prostoru</strong> na to, co je důležité</p>
                      </div>
                    </div>
                  </div>

                  <div className="text-center pt-6 border-t-2 border-teal-100 mt-10">
                    <p className="text-2xl font-bold text-gray-900 mb-8">
                      A my hledáme partnery, kteří to cítí stejně.
                    </p>
                    
                    <div className="space-y-4 text-left max-w-2xl mx-auto bg-gradient-to-br from-white to-teal-50 rounded-2xl p-8 border-2 border-teal-200">
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-2xl mt-1 flex-shrink-0"></i>
                        <p className="text-lg text-gray-900">Máte prostor</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-2xl mt-1 flex-shrink-0"></i>
                        <p className="text-lg text-gray-900">Máte čas a chuť dělat věci poctivě</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-2xl mt-1 flex-shrink-0"></i>
                        <p className="text-lg text-gray-900">Chcete být součástí lokální služby s přesahem</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-2xl mt-1 flex-shrink-0"></i>
                        <p className="text-lg text-gray-900">Sdílíte náš důraz na kvalitu, klid a lidský přístup</p>
                      </div>
                    </div>

                    <div className="mt-10 space-y-3">
                      <p className="text-2xl font-bold text-gray-900">
                        Nejsme další služba.
                      </p>
                      <p className="text-3xl font-bold text-teal-600">
                        Jsme nový standard.
                      </p>
                      <p className="text-xl text-gray-700 mt-6">
                        Pojďme se potkat.
                      </p>
                      <p className="text-xl text-gray-700">
                        Možná právě u vás vznikne další Vyžehlené.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
